// Static configuration for the property-listing-request wizard: ownership-role
// options (values mirror the seller-service PropertyOwnershipRole enum), the
// ordered step list driving the progress rail, and title-deed upload limits
// (mirrors the seller-service title-deed validation).

import type { SelectOption } from '@shared/types/form'

export const OWNERSHIP_ROLE = {
  SOLE_OWNER: 'sole_owner',
  CO_OWNER: 'co_owner',
  REAL_ESTATE_AGENT: 'real_estate_agent',
  AUTHORISED_REPRESENTATIVE: 'authorised_representative',
} as const

export type OwnershipRole = (typeof OWNERSHIP_ROLE)[keyof typeof OWNERSHIP_ROLE]

// Values mirror the shared `property_type` PG enum (seller-service PropertyType).
// Required at submit: `properties.property_type` is NOT NULL, so a request without
// one can never be promoted into a property — and once a request reaches owner
// onboarding there is no revision path left to add it.
//
// The full enum lives here because read-only surfaces still have to label requests
// submitted under any of these values; what a seller may *pick* today is the
// narrower `SELECTABLE_PROPERTY_TYPES` below.
export const PROPERTY_TYPE = {
  RESIDENTIAL: 'residential',
  COMMERCIAL: 'commercial',
  APARTMENT: 'apartment',
  VILLA: 'villa',
  BUILDING: 'building',
  STORE: 'store',
  WORKSHOP: 'workshop',
  LAND: 'land',
  UNIT: 'unit',
} as const

export type PropertyType = (typeof PROPERTY_TYPE)[keyof typeof PROPERTY_TYPE]

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  [PROPERTY_TYPE.RESIDENTIAL]: 'Residential',
  [PROPERTY_TYPE.COMMERCIAL]: 'Commercial',
  [PROPERTY_TYPE.APARTMENT]: 'Apartment',
  [PROPERTY_TYPE.VILLA]: 'Villa',
  [PROPERTY_TYPE.BUILDING]: 'Building',
  [PROPERTY_TYPE.STORE]: 'Store',
  [PROPERTY_TYPE.WORKSHOP]: 'Workshop',
  [PROPERTY_TYPE.LAND]: 'Land',
  [PROPERTY_TYPE.UNIT]: 'Unit',
}

// Only these two are offered on the create / revise request. The platform lists
// individual units and villas today; the remaining enum values exist for older
// requests and for admin-created properties, so they stay labelled above but are
// not selectable here. A request being revised whose stored type is outside this
// set seeds empty and the seller re-picks (see the store's
// `toSelectablePropertyType`).
export const SELECTABLE_PROPERTY_TYPES = [PROPERTY_TYPE.UNIT, PROPERTY_TYPE.VILLA] as const

export type SelectablePropertyType = (typeof SELECTABLE_PROPERTY_TYPES)[number]

const SELECTABLE_PROPERTY_TYPE_VALUES = new Set<string>(SELECTABLE_PROPERTY_TYPES)

export function isSelectablePropertyType(value: string | null): value is SelectablePropertyType {
  return value !== null && SELECTABLE_PROPERTY_TYPE_VALUES.has(value)
}

export const PROPERTY_TYPE_OPTIONS: { label: string; value: SelectablePropertyType }[] =
  SELECTABLE_PROPERTY_TYPES.map((value) => ({ label: PROPERTY_TYPE_LABELS[value], value }))

function isPropertyType(value: string): value is PropertyType {
  return value in PROPERTY_TYPE_LABELS
}

// Read-only surfaces (the request detail screen) render the stored enum value,
// which may be a type the wizard no longer offers — hence the full label map
// above rather than one derived from the selectable options. Unknown values fall
// back to a prettified form of the raw value rather than disappearing.
export function propertyTypeLabelOf(value: string | null): string | null {
  if (!value) return null
  return isPropertyType(value) ? PROPERTY_TYPE_LABELS[value] : prettifyEnumValue(value)
}

export interface OwnershipRoleOption {
  value: OwnershipRole
  title: string
  description: string
  icon: string
}

export const OWNERSHIP_ROLE_OPTIONS: OwnershipRoleOption[] = [
  {
    value: OWNERSHIP_ROLE.SOLE_OWNER,
    title: 'Sole Owner',
    description: 'You hold 100% ownership and make all decisions.',
    icon: 'user',
  },
  {
    value: OWNERSHIP_ROLE.CO_OWNER,
    title: 'Co-Owner',
    description: "You share ownership. We'll need signatures from all parties.",
    icon: 'users',
  },
  {
    value: OWNERSHIP_ROLE.REAL_ESTATE_AGENT,
    title: 'Real Estate Agent',
    description: 'Licensed broker who finds buyers/tenants.',
    icon: 'briefcase',
  },
  {
    value: OWNERSHIP_ROLE.AUTHORISED_REPRESENTATIVE,
    title: 'Authorised Representative',
    description:
      'Someone with Power of Attorney (POA) who can legally sign and transfer property for you.',
    icon: 'shield',
  },
]

const OWNERSHIP_ROLE_LABELS: Record<string, string> = Object.fromEntries(
  OWNERSHIP_ROLE_OPTIONS.map((option) => [option.value, option.title]),
)

export function ownershipRoleLabelOf(value: string | null): string | null {
  if (!value) return null
  return OWNERSHIP_ROLE_LABELS[value] ?? prettifyEnumValue(value)
}

// snake_case enum value → "Title Case" words. Shared by both label lookups above
// as the unknown-value fallback so a backend enum addition still reads sensibly.
function prettifyEnumValue(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export type ListingStepKey = 'ownership' | 'titleDeed' | 'verify' | 'pricing'

export const STEP_KEYS: ListingStepKey[] = ['ownership', 'titleDeed', 'verify', 'pricing']

export interface WizardStepMeta {
  key: ListingStepKey
  title: string
  subtitle: string
}

export const LISTING_STEPS: WizardStepMeta[] = [
  { key: 'ownership', title: 'Getting Started', subtitle: 'Basic ownership & status' },
  { key: 'titleDeed', title: 'Title Deed', subtitle: 'Smart AI extraction' },
  { key: 'verify', title: 'Verify Details', subtitle: 'Legal & financial data' },
  { key: 'pricing', title: 'Listing Profile', subtitle: 'Set your price' },
]

const BYTES_PER_KB = 1024
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB

export const TITLE_DEED_MAX_FILE_SIZE_MB = 30
export const TITLE_DEED_MAX_FILE_SIZE_BYTES = TITLE_DEED_MAX_FILE_SIZE_MB * BYTES_PER_MB
export const TITLE_DEED_ACCEPT =
  '.png,.jpg,.jpeg,.heif,.heic,.pdf,image/png,image/jpeg,image/heif,image/heic,application/pdf'

// Mirrors the seller-service rental-agreement limits. Kept as its own set of
// constants rather than aliases of the deed's so tightening one never silently
// tightens the other.
export const RENTAL_AGREEMENT_MAX_FILE_SIZE_MB = 30
export const RENTAL_AGREEMENT_MAX_FILE_SIZE_BYTES =
  RENTAL_AGREEMENT_MAX_FILE_SIZE_MB * BYTES_PER_MB
export const RENTAL_AGREEMENT_ACCEPT =
  '.png,.jpg,.jpeg,.heif,.heic,.pdf,image/png,image/jpeg,image/heif,image/heic,application/pdf'

export const DEFAULT_LISTING_CURRENCY = 'AED'

// Every listing is UAE property today, so the country selector preselects this
// ISO-2 code from the countries lookup instead of making the seller find it.
export const DEFAULT_LISTING_COUNTRY_ISO2 = 'AE'

// Field length ceilings — mirror the seller-service CreateListingRequestRequestDto
// column limits so the client rejects over-length input before the round trip.
export const LISTING_FIELD_MAX_LENGTH = {
  certificateNumber: 100,
  propertyName: 255,
  propertyNumber: 50,
  buildingName: 255,
  locationArea: 255,
  plotNumber: 50,
  municipalityNumber: 100,
  addressLine1: 255,
  city: 100,
  ownerFullName: 255,
  ownerEmail: 255,
  ownerMobileNumber: 20,
  ownerEmiratesId: 15,
} as const

// Owner mobile numbers are entered through the tel input in international mode
// and submitted as E.164 — mirrors the seller-service OwnerInputDto regex.
export const OWNER_MOBILE_DEFAULT_COUNTRY = 'AE'
export const OWNER_MOBILE_E164_REGEX = /^\+[1-9]\d{7,14}$/

// Emirates IDs are entered with or without the card's display dashes and submitted
// digits-only — mirrors the seller-service OwnerInputDto regex, which in turn
// mirrors the emirates_id CHECK constraint.
export const OWNER_EMIRATES_ID_REGEX = /^\d{15}$/
export const OWNER_EMIRATES_ID_PLACEHOLDER = '784-1990-1234567-1'

// Whether a declared owner is a person or a company. Mirrors
// `property_owners.property_owner_type` and seller-service's `PropertyOwnerType`.
// The value decides which onboarding flow and identity-verification level the
// owner's invite puts them on, so it has to be right before the link goes out.
export const OWNER_TYPE = {
  INDIVIDUAL: 'individual',
  COMPANY: 'company',
} as const

export type OwnerType = (typeof OWNER_TYPE)[keyof typeof OWNER_TYPE]

// Company is listed but not selectable: company-owned listings need a corporate
// onboarding + identity-verification path the platform doesn't run yet, so a
// company owner declared here would stall at the invite. Kept visible rather than
// removed so the seller can see it's coming, and so the enum stays legible next to
// the OCR path, which can still read `company` off a title deed.
export const OWNER_TYPE_OPTIONS: SelectOption[] = [
  { label: 'Individual', value: OWNER_TYPE.INDIVIDUAL },
  { label: 'Company (coming soon)', value: OWNER_TYPE.COMPANY, disabled: true },
]

// Positive money amount, up to 2 decimals, rejects zero — mirrors the BE
// POSITIVE_MONEY_REGEX. Area is non-negative with up to 2 decimals (AREA_REGEX).
export const LISTING_PRICE_REGEX = /^(?!0+(\.0+)?$)\d{1,16}(\.\d{1,2})?$/
export const LISTING_AREA_REGEX = /^\d{1,8}(\.\d{1,2})?$/
export const OWNERSHIP_PERCENTAGE_REGEX = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/

export const FULL_OWNERSHIP_PERCENTAGE = 100
